import { Component } from '@theme/component';

/**
 * @typedef {Object} StickySubmenuRefs
 * @property {HTMLElement} stickyMenu - The sticky menu container
 */

/**
 * A custom element that manages a sticky sub-menu.
 * Shows as sticky when the section is scrolled past.
 *
 * @extends {Component<StickySubmenuRefs>}
 */
class StickySubmenuComponent extends Component {
  requiredRefs = ['stickyMenu'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setupIntersectionObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /**
   * Sets up scroll detection for sticky behavior
   */
  #setupIntersectionObserver() {
    const { stickyMenu } = this.refs;
    let placeholder = null;

    const checkPosition = () => {
      const rect = this.getBoundingClientRect();

      if (rect.top <= 0 && rect.bottom > 0) {
        // Element has reached the top - make it fixed
        if (!placeholder) {
          placeholder = document.createElement('div');
          placeholder.style.height = `${stickyMenu.offsetHeight}px`;
          this.insertBefore(placeholder, stickyMenu);
        }
        stickyMenu.dataset.stuck = 'true';
      } else if (rect.top > 0) {
        // Element is below the top - keep it normal
        if (placeholder) {
          placeholder.remove();
          placeholder = null;
        }
        stickyMenu.dataset.stuck = 'false';
      }
    };

    // Check on scroll
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          checkPosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    checkPosition(); // Check initial position
  }
}

if (!customElements.get('sticky-submenu')) {
  customElements.define('sticky-submenu', StickySubmenuComponent);
}
